import React from 'react';

import { Box, Container, Link, Typography } from '@mui/material';

import PageHero from '../components/stitch/PageHero';
import { cardSx } from '../components/stitch/Section';
import { tokens } from '../theme';

const emailLink = <Link href='mailto:info@scaledtech.consulting'>info@scaledtech.consulting</Link>;

// Built to the Stitch "Privacy Notice" screen (project 1587770953174176333): an in-page list
// beside white section cards. The notice text is unchanged.
const sections = [
  ['contact-inquiries', 'Contact inquiries'],
  ['website-operation', 'Website operation and analytics'],
  ['google-calendar-data', 'Google Calendar access for Scaled Tech Booking'],
  ['information-retention', 'How long information is kept'],
  ['requests-and-contact', 'Requests and contact'],
] as const;

const sectionSx = { ...cardSx, p: { xs: 3, md: 5 }, scrollMarginTop: '96px' } as const;

function PrivacyNotice() {
  return (
    <Container sx={{ overflowWrap: 'anywhere' }}>
      <PageHero
        eyebrow='Privacy'
        title='Privacy notice'
        intro='Scaled Tech Consulting LLC operates this website. This notice explains how information is used when you visit the site or contact us through it.'
      />

      <Box
        sx={{
          p: { xs: 2, md: 4 },
          borderRadius: 3,
          bgcolor: tokens.surfaceLow,
          display: 'grid',
          gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: '17rem minmax(0, 48rem)' },
          alignItems: 'start',
          gap: { xs: 3, lg: 5 },
        }}
      >
        <Box
          component='nav'
          aria-labelledby='privacy-contents'
          sx={{ ...cardSx, p: 3, position: { lg: 'sticky' }, top: { lg: '96px' } }}
        >
          <Typography
            id='privacy-contents'
            component='h2'
            variant='labelCaps'
            sx={{ display: 'block', color: tokens.accentText, mb: 1 }}
          >
            On this page
          </Typography>
          <Box
            component='ul'
            sx={{ listStyle: 'none', m: 0, p: 0 }}
          >
            {sections.map(([id, label]) => (
              <li key={id}>
                <Box
                  component='a'
                  href={`#${id}`}
                  sx={{
                    display: 'block',
                    py: 0.75,
                    color: tokens.onSurfaceVariant,
                    fontSize: '0.875rem',
                    lineHeight: 20 / 14,
                    fontWeight: 500,
                    textDecoration: 'none',
                    '&:hover': { color: tokens.primary, textDecoration: 'underline' },
                  }}
                >
                  {label}
                </Box>
              </li>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gap: 3, minWidth: 0 }}>
          <Box
            component='section'
            id='contact-inquiries'
            sx={sectionSx}
          >
            <Typography
              component='h2'
              variant='h3'
            >
              Contact inquiries
            </Typography>
            <Typography sx={{ mt: 2 }}>
              You can browse the site without creating an account. If you use the contact form, we
              ask for your name, email address, and message, and you can optionally share the kind
              of work and timing. We use these details to respond to your inquiry and follow up on
              the matter you raise. Please do not include sensitive or confidential information in
              the form.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              The form uses Cloudflare Turnstile to help prevent automated submissions. Your browser
              interacts with Cloudflare, and the site checks a verification token with Cloudflare
              before accepting the form. If verification does not finish, you can contact us
              directly at {emailLink}.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              The site uses Cloudflare&apos;s email service to send the inquiry to our email
              address. Our receiving mailbox is provided through Apple iCloud Mail. If you email us
              directly, your message may also pass through Cloudflare&apos;s email routing service
              and the other email services involved in delivering it.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              If you book a call on the Contact page, the booking tool sends your name, email
              address and answers to our booking service, which runs on Cloudflare and also uses
              Turnstile. It checks our Google Calendar for free times, creates a Google Calendar
              invitation with a Google Meet link and sends it to your email address, and emails your
              details to us. The invitation contains only your email address and the meeting time,
              not what you typed. Google handles the invitation and the meeting under its own
              privacy policy. The booking service clears your name, email address and answers 30
              days after the meeting; the invitation and our copy of your details stay with our
              calendar and email as correspondence.
            </Typography>
          </Box>

          <Box
            component='section'
            id='website-operation'
            sx={sectionSx}
          >
            <Typography
              component='h2'
              variant='h3'
            >
              Website operation and analytics
            </Typography>
            <Typography
              component='h3'
              variant='h5'
              sx={{ mt: 3 }}
            >
              Hosting and security
            </Typography>
            <Typography sx={{ mt: 2 }}>
              Cloudflare helps serve and protect the website. In providing those services,
              Cloudflare may process technical information about a request, such as an IP address,
              browser information, and request or security data. Cloudflare may use security cookies
              or similar technologies when needed to protect or operate its services.
            </Typography>
            <Typography
              component='h3'
              variant='h5'
              sx={{ mt: 3 }}
            >
              Website analytics
            </Typography>
            <Typography sx={{ mt: 2 }}>
              We use Cloudflare Web Analytics to understand page visits and website performance. It
              can report information such as the page visited, referring site, country, browser,
              device type, and page-load performance. Our current setting is configured to exclude
              visitor data in the European Union; Cloudflare says the measurement script is not
              injected for visitors from the EU under that setting. This exclusion does not cover
              every country in Europe.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              To see which parts of the site are useful, we count clicks on a few buttons, such as
              requesting an assessment or opening the capability statement. Your browser sends only
              the button&apos;s name to our own server; we don&apos;t set cookies or store
              identifiers or IP addresses with these counts.
            </Typography>
            <Typography
              component='h3'
              variant='h5'
              sx={{ mt: 3 }}
            >
              Error and performance reporting
            </Typography>
            <Typography sx={{ mt: 2 }}>
              We use a self-hosted Sentry service to help identify website errors and investigate
              performance. Reports contain limited technical information, such as error locations
              and timing measurements. We configure the site to exclude contact-form contents and
              URL query values from these reports. Connections to this service pass through
              Cloudflare.
            </Typography>
            <Typography
              component='h3'
              variant='h5'
              sx={{ mt: 3 }}
            >
              Session recordings
            </Typography>
            <Typography sx={{ mt: 2 }}>
              We record a sample of visits and may also save a buffered recording when a website
              error occurs. These simplified recordings include page structure, visited public
              pages, pointer movements, clicks and scrolling. Text and form inputs are masked,
              although masked text length may remain visible; images and other media are blocked.
              Recordings stop when the contact page or an unrecognized page is reached. A recording
              session identifier is stored in the browser&apos;s session storage for the tab.
            </Typography>
            <Typography
              component='h3'
              variant='h5'
              sx={{ mt: 3 }}
            >
              Accessibility display preferences
            </Typography>
            <Typography sx={{ mt: 2 }}>
              The website&apos;s accessibility options apply in the current browser tab until it is
              reloaded or closed. If you turn on &ldquo;Remember on this device&rdquo;, your choices
              are saved in this browser&apos;s local storage so they apply on your next visit.
              Turning that option off, or choosing &ldquo;Reset all&rdquo;, removes them. They are
              never sent to us.
            </Typography>
          </Box>

          <Box
            component='section'
            id='google-calendar-data'
            aria-labelledby='google-calendar-data-heading'
            sx={sectionSx}
          >
            <Typography
              id='google-calendar-data-heading'
              component='h2'
              variant='h3'
            >
              Google Calendar access for Scaled Tech Booking
            </Typography>
            <Typography sx={{ mt: 2 }}>
              Scaled Tech Booking is our booking service at{' '}
              <Link href='https://booking.scaledtech.consulting/'>
                booking.scaledtech.consulting
              </Link>
              . A business that uses it, including Scaled Tech Consulting itself, connects its own
              Google account once so the service can schedule calls on that account&apos;s calendar.
              Visitors who book a call do not sign in to Google and do not give the service access
              to their own Google account. This section describes the Google user data of a
              connected account.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              What the service accesses: it reads busy time ranges from the calendars the account
              chooses, to offer only open times; it does not read event titles, descriptions or
              guests. It creates one event with a Google Meet link for each booking on the connected
              account&apos;s calendar, invites the visitor, and deletes that event if the booking is
              cancelled; it does not read, change or delete any other events. It reads the
              account&apos;s email address to confirm the right account was connected.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              What is stored: the connected account&apos;s email address and its Google refresh
              token, encrypted, in Cloudflare, which hosts the service; and, for each booking, the
              Google event identifier. Busy times are used to work out open times and are not
              stored. Short-lived access tokens are kept only in memory.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              How it is used and shared: Google user data is used only to provide the booking
              service to the connected account. It is not sold, not used for advertising, not used
              to train artificial intelligence or machine learning models, and not shared with
              anyone except the service providers needed to run the service, or where the law
              requires it. People do not read it except to keep the service secure, to comply with
              the law, or at the account owner&apos;s request. Scaled Tech Booking&apos;s use and
              transfer of information received from Google APIs to any other app will adhere to the{' '}
              <Link href='https://developers.google.com/terms/api-services-user-data-policy'>
                Google API Services User Data Policy
              </Link>
              , including the Limited Use requirements.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              Removing access: the account owner can revoke access at any time from the Google
              Account&apos;s{' '}
              <Link href='https://myaccount.google.com/connections'>third-party connections</Link>{' '}
              page, which stops the service using the stored token. To have the stored token and
              account details deleted, email {emailLink}; we delete them and confirm by email.
            </Typography>
          </Box>

          <Box
            component='section'
            id='information-retention'
            sx={sectionSx}
          >
            <Typography
              component='h2'
              variant='h3'
            >
              How long information is kept
            </Typography>
            <Typography sx={{ mt: 2 }}>
              We retain inquiry correspondence to respond, follow up, and handle any resulting
              business or applicable recordkeeping needs. The time can depend on the inquiry and
              whether it becomes part of a client or other record. Messages may also be held by our
              email providers or in backups, according to the systems involved. Deleting an
              active-mailbox message does not necessarily remove every backup copy immediately.
            </Typography>
          </Box>

          <Box
            component='section'
            id='requests-and-contact'
            sx={sectionSx}
          >
            <Typography
              component='h2'
              variant='h3'
            >
              Requests and contact
            </Typography>
            <Typography sx={{ mt: 2 }}>
              For questions about this notice or to request access to, correction of, or deletion of
              information relating to your use of the website, email {emailLink}. We will consider
              requests under applicable law and may need to verify your identity before acting.
              Rights and any exceptions depend on the law that applies to the request. Depending on
              the information and applicable law, we may need to retain certain records or rely on a
              service provider to address information held in its systems.
            </Typography>
            <Typography sx={{ mt: 2 }}>
              Links to other websites are governed by those sites&apos; own privacy notices.
            </Typography>
          </Box>
        </Box>
      </Box>
    </Container>
  );
}

export default PrivacyNotice;
